import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export const userFeatureKey = 'user'

export const userInitialState: UserRedux = {}

export type User = {
  /**
   * Auth0 user id. Auth0 refers to this as sub.
   */
  auth0Id: string
  nickname?: string
  name?: string
  email?: string
  org_id?: string
  clearwaterData?: {
    legacyUserId?: number
    /**
     * Clearwater's user table's client id
     */
    clientId?: number
    rootClientId?: number
    name?: string
    realm?: string
  }
  roleDetails?: Record<string, unknown>
}

export type UserAccessPanelPolicy = {
  accessAccountDetailsPanel: boolean
  accessAccountsPanel: boolean
  accessApprovalsListPanel: boolean
  accessBreaksAndVariancesPanel: boolean
  accessBulkClearwaterTransactions: boolean
  accessBulkExternalTransactionsPanel: boolean
  accessCorporateActionsPanel: boolean
  accessDevtoolsPanel: boolean
  accessDistributionsPanel: boolean
  accessExternalFileManagerPanel: boolean
  accessExternalFileViewerPanel: boolean
  accessJobsPanel: boolean
  accessPositionsPanel: boolean
  accessPricingPanel: boolean
  accessRawTradeSummaryPanel: boolean
  accessReconDashboardPanel: boolean
  accessRuleOverviewPanel: boolean
  accessSecurityDetailPanel: boolean
  accessSecurityMappingPanel: boolean
  accessSmartSuspensePanel: boolean
  accessTransactionDetailPanel: boolean
  accessTransactionsPanel: boolean
  accessExternalAccountsPanel: boolean
  accessOutputManagerPanel: boolean
  accessClarityOpsPanel: boolean
}

export type UserPolicy = UserAccessPanelPolicy & {
  accessDeveloperTools: boolean
  bulkToolActions: boolean
  accessBetaFeatures: boolean
  runAccount: boolean
  enterCASLTx: boolean
  accessCopilot?: boolean
  accessCommentary?: boolean
  accessNotifications?: boolean
  /**
   * Restricted action request types the user is permitted to approve or deny
   * in the Approvals panel. Values come from RestrictedActionRequestTypes
   * (e.g. 'FORCE_RECON', 'RESTRICTED_BREAK'). A type missing from this list
   * means the user cannot approve/deny requests of that type.
   */
  approvableActionTypes?: string[]
}

export type UserRedux = {
  /**
   * User details from Auth0
   */
  user?: User
  userPolicy?: UserPolicy
  hasAcceptedInvitation?: boolean
}

export const userSlice = createSlice({
  name: userFeatureKey,
  initialState: userInitialState,
  reducers: {
    setCurrentUser: (oldState, action: PayloadAction<{ user: User }>) => {
      const user = action.payload.user
      return {
        ...oldState,
        user: user,
      }
    },
    setUserPolicy: (
      oldState,
      action: PayloadAction<{ userPolicy: UserPolicy }>,
    ) => ({
      ...oldState,
      userPolicy: action.payload.userPolicy,
    }),
    deleteCurrentUser: oldState => ({
      ...oldState,
      user: undefined,
    }),
  },
})

export const { setCurrentUser, setUserPolicy, deleteCurrentUser } =
  userSlice.actions

export default userSlice.reducer
